import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AppLayout, { PageHeader } from "../components/AppLayout";
import { StatusBadge } from "../components/SeverityChip";
import {
    listSystems,
    seedSystems,
    getStats,
    createSystem,
    deleteSystem,
    listSnapshots,
    restoreSnapshot,
    deleteSnapshot,
} from "../lib/api";
import {
    Plus,
    Database,
    Play,
    ChevronRight,
    Loader2,
    Sparkles,
    Trash2,
    Archive,
    RotateCcw,
} from "lucide-react";
import { toast } from "sonner";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    DialogFooter,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";

const SnapshotsPanel = ({ systems, onRestored }) => {
    const [snaps, setSnaps] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            setSnaps(await listSnapshots());
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        load();
    }, []);

    const handleRestore = async (snapId, systemId) => {
        if (!systemId) {
            toast.error("Pick a target system first");
            return;
        }
        setBusy(snapId);
        try {
            const r = await restoreSnapshot(snapId, systemId);
            toast.success(
                `Restored · ${r.restored.rules} rules · ${r.restored.findings} findings`,
            );
            onRestored?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Restore failed");
        } finally {
            setBusy(null);
        }
    };

    const handleDelete = async (snapId, label) => {
        if (!window.confirm(`Delete snapshot "${label}"?`)) return;
        await deleteSnapshot(snapId);
        toast.success("Snapshot deleted");
        load();
    };

    return (
        <div className="p-8 md:p-12 pt-0">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <div className="flex items-center gap-3">
                    <Archive size={14} className="text-neutral-500" />
                    <div className="overline">Saved demo snapshots</div>
                </div>
                <div className="mono text-[11px] text-neutral-500">
                    {snaps.length} snapshot{snaps.length === 1 ? "" : "s"}
                </div>
            </div>
            <div className="border border-hair bg-white">
                {loading && (
                    <div className="p-6 text-center text-neutral-500">
                        <Loader2 className="animate-spin inline" size={14} />
                    </div>
                )}
                {!loading && snaps.length === 0 && (
                    <div className="p-8 text-center text-neutral-500 text-sm">
                        No snapshots yet. Run a periodic review on any system and
                        click <span className="mono">Save snapshot</span> to reuse
                        the audit later — no LLM cost.
                    </div>
                )}
                {snaps.map((s) => (
                    <SnapshotRow
                        key={s.id}
                        snap={s}
                        systems={systems}
                        busy={busy === s.id}
                        onRestore={handleRestore}
                        onDelete={handleDelete}
                    />
                ))}
            </div>
        </div>
    );
};

const SnapshotRow = ({ snap, systems, busy, onRestore, onDelete }) => {
    const [target, setTarget] = useState(snap.source_system_id || "");
    return (
        <div
            className="grid grid-cols-12 gap-4 px-4 py-4 border-b border-hair last:border-b-0 items-center"
            data-testid={`snapshot-${snap.id}`}
        >
            <div className="col-span-4 min-w-0">
                <div className="heading font-medium tracking-tight truncate">
                    {snap.label}
                </div>
                <div className="text-xs text-neutral-500 mono mt-1 truncate">
                    {snap.system_name}
                    {snap.vendor ? ` · ${snap.vendor}` : ""} ·{" "}
                    {new Date(snap.created_at).toLocaleString()}
                </div>
            </div>
            <div className="col-span-4">
                <Select value={target} onValueChange={setTarget}>
                    <SelectTrigger className="h-9 text-sm" data-testid={`snap-target-${snap.id}`}>
                        <SelectValue placeholder="Restore to system…" />
                    </SelectTrigger>
                    <SelectContent>
                        {systems.map((s) => (
                            <SelectItem key={s.id} value={s.id}>
                                {s.name}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>
            <div className="col-span-3 flex justify-end gap-2">
                <button
                    data-testid={`restore-${snap.id}`}
                    disabled={busy}
                    onClick={() => onRestore(snap.id, target)}
                    className="inline-flex items-center gap-2 bg-[#0a0a0a] text-white text-xs px-3 py-2 rounded-sm hover:bg-[#262626] disabled:opacity-50"
                >
                    {busy ? (
                        <Loader2 className="animate-spin" size={12} />
                    ) : (
                        <RotateCcw size={12} />
                    )}
                    Restore
                </button>
            </div>
            <div className="col-span-1 flex justify-end">
                <button
                    onClick={() => onDelete(snap.id, snap.label)}
                    className="text-neutral-400 hover:text-[#991B1B]"
                    data-testid={`delete-snap-${snap.id}`}
                    title="Delete snapshot"
                >
                    <Trash2 size={14} />
                </button>
            </div>
        </div>
    );
};

const KpiCard = ({ label, value, sub, testid, accent }) => (
    <div
        className="p-8 border-r border-b border-hair bg-white"
        data-testid={testid}
    >
        <div className="overline mb-4">{label}</div>
        <div
            className={`heading text-5xl font-medium tracking-tighter leading-none ${
                accent ? "text-[#C2410C]" : ""
            }`}
        >
            {value}
        </div>
        {sub && <div className="text-xs text-neutral-500 mt-3 mono">{sub}</div>}
    </div>
);

const AddSystemDialog = ({ onCreated }) => {
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({
        name: "",
        full_name: "",
        vendor: "",
        version: "",
        owner: "",
        department: "",
        risk_level: "GxP-Critical",
        criticality: "High",
    });
    const [saving, setSaving] = useState(false);

    const submit = async () => {
        if (!form.name.trim()) {
            toast.error("System name required");
            return;
        }
        setSaving(true);
        try {
            const s = await createSystem(form);
            toast.success(`System ${s.name} created`);
            setOpen(false);
            onCreated?.();
        } catch (e) {
            toast.error("Failed to create system");
        } finally {
            setSaving(false);
        }
    };

    const fields = [
        ["name", "System Name (e.g. LIMS-PROD-01)", true],
        ["full_name", "Full Name"],
        ["vendor", "Vendor"],
        ["version", "Version"],
        ["owner", "Owner"],
        ["department", "Department"],
    ];

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <button
                    data-testid="btn-add-system"
                    className="inline-flex items-center gap-2 bg-[#0a0a0a] text-white text-sm px-4 py-2 rounded-sm hover:bg-[#262626]"
                >
                    <Plus size={14} /> Add System
                </button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <DialogTitle className="heading tracking-tight">
                        Register a GxP system
                    </DialogTitle>
                </DialogHeader>
                <div className="grid grid-cols-2 gap-4 py-2">
                    {fields.map(([k, label, req]) => (
                        <div
                            key={k}
                            className={k === "name" ? "col-span-2" : "col-span-1"}
                        >
                            <Label className="text-xs mono overline">{label}</Label>
                            <Input
                                data-testid={`new-system-${k}`}
                                value={form[k]}
                                onChange={(e) =>
                                    setForm({ ...form, [k]: e.target.value })
                                }
                                className="mt-1"
                            />
                        </div>
                    ))}
                    <div>
                        <Label className="text-xs mono overline">Risk Level</Label>
                        <Select
                            value={form.risk_level}
                            onValueChange={(v) => setForm({ ...form, risk_level: v })}
                        >
                            <SelectTrigger className="mt-1" data-testid="new-system-risk">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="GxP-Critical">
                                    GxP-Critical
                                </SelectItem>
                                <SelectItem value="GxP-Supporting">
                                    GxP-Supporting
                                </SelectItem>
                                <SelectItem value="Non-GxP">Non-GxP</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-xs mono overline">Criticality</Label>
                        <Select
                            value={form.criticality}
                            onValueChange={(v) => setForm({ ...form, criticality: v })}
                        >
                            <SelectTrigger className="mt-1" data-testid="new-system-crit">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="High">High</SelectItem>
                                <SelectItem value="Medium">Medium</SelectItem>
                                <SelectItem value="Low">Low</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>
                <DialogFooter>
                    <button
                        data-testid="btn-create-system"
                        onClick={submit}
                        disabled={saving}
                        className="inline-flex items-center gap-2 bg-[#C2410C] text-white text-sm px-4 py-2 rounded-sm disabled:opacity-50"
                    >
                        {saving && <Loader2 size={14} className="animate-spin" />}
                        Create system
                    </button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default function Dashboard() {
    const [systems, setSystems] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [seeding, setSeeding] = useState(false);
    const navigate = useNavigate();

    const load = async () => {
        setLoading(true);
        try {
            const [s, st] = await Promise.all([listSystems(), getStats()]);
            setSystems(s);
            setStats(st);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const handleSeed = async () => {
        setSeeding(true);
        try {
            const r = await seedSystems();
            toast.success(`Seeded ${r.inserted} demo systems`);
            load();
        } catch (e) {
            toast.error("Seed failed");
        } finally {
            setSeeding(false);
        }
    };

    const handleDelete = async (id, name) => {
        if (!window.confirm(`Delete ${name}?`)) return;
        await deleteSystem(id);
        toast.success(`${name} deleted`);
        load();
    };

    return (
        <AppLayout>
            <PageHeader
                eyebrow="Workspace · Audit Dashboard"
                title="Systems under GxP oversight"
                sub="Every GxP system in your estate — their validated state, compliance score and next periodic review."
                right={
                    <div className="flex gap-3 flex-wrap">
                        <button
                            data-testid="btn-seed-demo"
                            onClick={handleSeed}
                            disabled={seeding}
                            className="inline-flex items-center gap-2 text-sm border border-hair px-4 py-2 rounded-sm hover:bg-white disabled:opacity-50"
                        >
                            {seeding ? (
                                <Loader2 size={14} className="animate-spin" />
                            ) : (
                                <Sparkles size={14} />
                            )}
                            Seed demo data
                        </button>
                        <AddSystemDialog onCreated={load} />
                    </div>
                }
            />

            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 border-l border-hair">
                <KpiCard
                    label="Systems"
                    value={stats?.systems_total ?? "—"}
                    sub="Under periodic review"
                    testid="kpi-systems"
                />
                <KpiCard
                    label="Avg compliance"
                    value={`${stats?.avg_compliance_score ?? 0}%`}
                    sub="Across all systems"
                    accent
                    testid="kpi-compliance"
                />
                <KpiCard
                    label="Overdue / Due"
                    value={`${stats?.systems_overdue ?? 0} / ${stats?.systems_due ?? 0}`}
                    sub="Reviews pending"
                    testid="kpi-overdue"
                />
                <KpiCard
                    label="Open findings"
                    value={stats?.findings_total ?? 0}
                    sub={`${stats?.findings_critical ?? 0} critical`}
                    testid="kpi-findings"
                />
            </div>

            {/* Systems table */}
            <div className="p-8 md:p-12">
                <div className="flex items-center justify-between mb-4">
                    <div className="overline">System Inventory</div>
                    <div className="mono text-[11px] text-neutral-500">
                        {systems.length} records
                    </div>
                </div>
                <div className="border border-hair bg-white">
                    <div className="grid grid-cols-12 gap-4 px-4 py-3 border-b border-hair overline text-[10px]">
                        <div className="col-span-3">System</div>
                        <div className="col-span-2">Owner</div>
                        <div className="col-span-1">Risk</div>
                        <div className="col-span-2">Status</div>
                        <div className="col-span-1 text-right">Score</div>
                        <div className="col-span-2 text-right">Next review</div>
                        <div className="col-span-1 text-right">Action</div>
                    </div>

                    {loading && (
                        <div className="p-10 text-center text-neutral-500">
                            <Loader2 className="animate-spin inline" size={16} />
                        </div>
                    )}

                    {!loading && systems.length === 0 && (
                        <div className="p-12 text-center">
                            <Database
                                className="mx-auto text-neutral-300"
                                size={36}
                            />
                            <p className="text-sm text-neutral-500 mt-4">
                                No systems yet. Seed demo data or add your first
                                system.
                            </p>
                        </div>
                    )}

                    {systems.map((s) => (
                        <div
                            key={s.id}
                            data-testid={`system-row-${s.name}`}
                            className="grid grid-cols-12 gap-4 px-4 py-4 border-b border-hair last:border-b-0 items-center hover:bg-[#fafafa] transition-colors"
                        >
                            <div className="col-span-3">
                                <Link
                                    to={`/systems/${s.id}`}
                                    className="heading font-medium tracking-tight hover:text-[#C2410C] transition-colors"
                                >
                                    {s.name}
                                </Link>
                                <div className="text-xs text-neutral-500 mt-1">
                                    {s.full_name || "—"}
                                    {s.vendor && ` · ${s.vendor}`}
                                    {s.version && ` v${s.version}`}
                                </div>
                            </div>
                            <div className="col-span-2 text-sm">
                                {s.owner || "—"}
                                <div className="text-xs text-neutral-500 mono">
                                    {s.department}
                                </div>
                            </div>
                            <div className="col-span-1 text-xs mono tracking-wide">
                                {s.risk_level}
                            </div>
                            <div className="col-span-2">
                                <StatusBadge value={s.status} />
                            </div>
                            <div className="col-span-1 text-right num font-medium">
                                {s.compliance_score > 0
                                    ? `${s.compliance_score}%`
                                    : "—"}
                            </div>
                            <div className="col-span-2 mono text-xs text-neutral-600 text-right">
                                {s.next_review_date
                                    ? new Date(
                                          s.next_review_date,
                                      ).toLocaleDateString()
                                    : "—"}
                            </div>
                            <div className="col-span-1 flex justify-end gap-2">
                                <button
                                    onClick={() => handleDelete(s.id, s.name)}
                                    className="text-neutral-400 hover:text-[#991B1B]"
                                    data-testid={`delete-${s.name}`}
                                    title="Delete"
                                >
                                    <Trash2 size={14} />
                                </button>
                                <button
                                    data-testid={`open-${s.name}`}
                                    onClick={() =>
                                        navigate(`/systems/${s.id}`)
                                    }
                                    className="inline-flex items-center gap-1 text-xs text-[#C2410C] hover:text-[#9A3412]"
                                >
                                    Open <ChevronRight size={12} />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
            <SnapshotsPanel systems={systems} onRestored={load} />
        </AppLayout>
    );
}
